import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import clientService, { ClientData } from '../../services/Clients_service';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import { isValidRcs } from '../../utils/functions/validateRCSNumber';
import { isValidPostalCode } from '../../utils/functions/validatePostalCode';
import { fetchPostalCodeDetails } from '../../utils/functions/fetchPostalCodeDetails';
import '../../css/SendToClient.css';

// Options de professions
const PROFESSION_OPTIONS: Record<string, string[]> = {
  "Professions juridiques": [
    "Administrateur / Mandataire judiciaire",
    "Avocat",
    "Clerc / Formaliste de notaire",
    "Greffier / Assistant de greffe",
    "Huissier de justice / Commissaire de justice",
    "Juriste d'entreprise",
    "Notaire",
  ],
  "Professions comptables": [
    "Cabinet comptable / Conseil en gestion",
    "Commissaire aux comptes",
    "Expert-comptable",
    "Gestionnaire de paie / RAF",
  ],
  "Créateurs et dirigeants d'entreprise": [
    "Dirigeant d'entreprise",
    "Entrepreneur individuel / Auto-entrepreneur",
    "Office manager / Responsable administratif",
  ],
  "Intermédiaires et formalistes": [
    "Agence de communication / Publicité",
    "Cabinet de formalités juridiques",
    "Formaliste en annonces légales",
    "Secrétaire juridique indépendant",
    "Société de domiciliation",
  ],
  "Autres professions": [
    "Administration / Assistance administrative",
    "Ressources humaines",
    "Responsable juridique",
    "Consultant indépendant",
  ],
  "Autre": [
    "Autre (à préciser)"
  ]
};

interface AddClientModalProps {
  show: boolean;
  onClose: () => void;
  onClientAdded: (client: ClientData) => void;
  editingClient?: ClientData | null;
}

// Type pour la civilité
type Civility = 'Mr' | 'Mme' | undefined;

const AddClientModal: React.FC<AddClientModalProps> = ({ 
  show, 
  onClose, 
  onClientAdded,
  editingClient 
}) => {
  const [formData, setFormData] = useState<Partial<ClientData>>({
    client_type: 'societe',
    email: '',
    adresse: '',
    telephone: '',
    civilite: undefined,
    nom: undefined,
    prenom: undefined,
    siren: '',
    raison_sociale: '',
    tva_intra: '',
    // code_postal: '',
    // ville: '',
    profession: undefined,
    show_in_directory: false,
    status: 'active'
  });

  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [customProfession, setCustomProfession] = useState<string>('');
  const [showCustomProfession, setShowCustomProfession] = useState<boolean>(false);
  
  const [loading, setLoading] = useState(false);
  const [loadingSiren, setLoadingSiren] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [cities, setCities] = useState<string[]>([]);

  // Réinitialiser le formulaire quand la modal s'ouvre
  useEffect(() => {
    if (show) {
      if (editingClient) {
        setFormData({
          client_type: 'societe',
          email: editingClient.email || '',
          adresse: editingClient.adresse || '',
          telephone: editingClient.telephone || '',
          civilite: (editingClient.civilite === 'Mr' || editingClient.civilite === 'Mme') 
            ? editingClient.civilite 
            : undefined,
          nom: editingClient.nom || undefined,
          prenom: editingClient.prenom || undefined,
          siren: editingClient.siren || '',
          raison_sociale: editingClient.raison_sociale || '',
          tva_intra: editingClient.tva_intra || '',
          // code_postal: editingClient.code_postal || '',
          // ville: editingClient.ville || '',
          profession: editingClient.profession || undefined,
          show_in_directory: editingClient.show_in_directory || false,
          status: editingClient.status || 'active'
        });

        // Charger les villes si le code postal est présent
        // if (editingClient.code_postal && isValidPostalCode(editingClient.code_postal)) {
        //   handlePostalCodeChange(editingClient.code_postal);
        // }

        // Gérer la profession pour l'affichage
        if (editingClient.profession) {
          let foundCategory = '';
          
          Object.entries(PROFESSION_OPTIONS).forEach(([category, professions]) => {
            if (professions.includes(editingClient.profession || '')) {
              foundCategory = category;
            }
          });
          
          if (foundCategory) {
            setSelectedCategory(foundCategory);
            setShowCustomProfession(false);
          } else {
            setSelectedCategory('Autre');
            setCustomProfession(editingClient.profession);
            setShowCustomProfession(true);
          }
        }
      } else {
        // Réinitialiser pour une nouvelle société
        setFormData({
          client_type: 'societe',
          email: '',
          adresse: '',
          telephone: '',
          civilite: undefined,
          nom: undefined,
          prenom: undefined,
          siren: '',
          raison_sociale: '',
          tva_intra: '',
          // code_postal: '',
          // ville: '',
          profession: undefined,
          show_in_directory: false,
          status: 'active'
        });
        setSelectedCategory('');
        setCustomProfession('');
        setShowCustomProfession(false);
        setCities([]);
      }
      setErrors({});
    }
  }, [show, editingClient]);

  // Fonction pour gérer le changement du code postal
  const handlePostalCodeChange = async (value: string) => {
    // Ne garder que les chiffres et limiter à 5 caractères
    const cleanValue = value.replace(/\D/g, '').slice(0, 5);
    
    setFormData((prev: Partial<ClientData>) => ({
      ...prev,
      code_postal: cleanValue
    }));
    
    if (isValidPostalCode(cleanValue)) {
      try {
        const postalDetails = await fetchPostalCodeDetails(cleanValue);
        const fetchedCities = postalDetails?.cities || [];
        setCities(fetchedCities);
        
        // Auto-sélectionner la première ville si disponible
        if (fetchedCities.length > 0) {
          setFormData((prev: Partial<ClientData>) => ({
            ...prev,
            ville: fetchedCities[0]
          }));
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des villes:', error);
        setCities([]);
      }
    } else {
      setCities([]);
    }
  };

  // Fonction pour gérer la saisie du SIREN et récupérer les infos
  const handleSirenChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const siren = e.target.value.replace(/\D/g, '').slice(0, 9);
    
    setFormData((prev: Partial<ClientData>) => ({
      ...prev,
      siren
    }));
    
    // Si le SIREN est valide (9 chiffres), récupérer les infos
    if (isValidRcs(siren)) {
      setLoadingSiren(true);
      try {
        const response = await axios(`/rne/${siren}`);
        const nafCode = await axios(`/rne/naf/${siren}`);
        
        if (response && response.data?.data) {
          const data = response.data.data;
          
          // Remplir automatiquement les champs
          setFormData((prev: Partial<ClientData>) => ({
            ...prev,
            raison_sociale: data.companyName || prev.raison_sociale,
            adresse: data.address || prev.adresse,
            tva_intra: nafCode.data?.data?.vat_number || prev.tva_intra,
          }));
          
          // Gérer le code postal si présent dans les données
          if (data.postalCode) {
            await handlePostalCodeChange(data.postalCode);
          }
          
          toast.success('Informations de la société récupérées avec succès');
        }
      } catch (error: any) {
        console.error('Erreur lors de la récupération des données SIREN:', error);
        toast.warning('Impossible de récupérer les informations de la société');
      } finally {
        setLoadingSiren(false);
      }
    }
  };

  // Validation spécifique pour les sociétés
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Email
    if (!formData.email?.trim()) {
      newErrors.email = 'Email requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    // Adresse
    if (!formData.adresse?.trim()) {
      newErrors.adresse = 'Adresse requise';
    }

    // Téléphone
    if (!formData.telephone?.trim()) {
      newErrors.telephone = 'Téléphone requis';
    } else {
      const phoneRegex = /^(0\d{9}|\+33\d{9}|\+[1-9]\d{6,14})$/;
      if (!phoneRegex.test(formData.telephone.replace(/\s/g, ''))) {
        newErrors.telephone = 'Format invalide (ex: 0123456789 ou +33123456789)';
      }
    }

    // SIREN
    if (!formData.siren?.trim()) {
      newErrors.siren = 'SIREN requis';
    } else {
      const sirenClean = formData.siren.replace(/\s/g, '');
      if (!/^\d{9}$/.test(sirenClean)) {
        newErrors.siren = 'Le SIREN doit contenir 9 chiffres';
      }
    }

    // Raison sociale
    if (!formData.raison_sociale?.trim()) {
      newErrors.raison_sociale = 'Raison sociale requise';
    }

    // TVA intracommunautaire
    if (!formData.tva_intra?.trim()) {
      newErrors.tva_intra = 'TVA intracommunautaire requise';
    }

    // Code postal
    // if (!formData.code_postal?.trim()) {
    //   newErrors.code_postal = 'Code postal requis';
    // } else if (!isValidPostalCode(formData.code_postal)) {
    //   newErrors.code_postal = 'Code postal invalide (5 chiffres)';
    // }

    // // Ville
    // if (!formData.ville?.trim()) {
    //   newErrors.ville = 'Ville requise';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev: Partial<ClientData>) => ({
        ...prev,
        [name]: checked
      }));
    } else {
      let processedValue: string | undefined = value;
      
      if (name === 'siren') {
        // Le SIREN est géré par handleSirenChange
        return;
      } else if (name === 'telephone') {
        processedValue = value.replace(/[^\d\s+]/g, '');
      } else if (name === 'code_postal') {
        handlePostalCodeChange(value);
        return;
      } else if (name === 'civilite') {
        processedValue = value;
      } else if (name === 'nom' || name === 'prenom' || name === 'profession') {
        processedValue = value || undefined;
      }
      
      setFormData((prev: Partial<ClientData>) => ({
        ...prev,
        [name]: processedValue
      }));
    }
    
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value;
    setSelectedCategory(category);
    
    if (category === 'Autre') {
      setShowCustomProfession(true);
      setFormData((prev: Partial<ClientData>) => ({
        ...prev,
        profession: customProfession || undefined
      }));
    } else {
      setShowCustomProfession(false);
      setFormData((prev: Partial<ClientData>) => ({
        ...prev,
        profession: undefined
      }));
    }
  };

  const handleProfessionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const profession = e.target.value;
    setFormData((prev: Partial<ClientData>) => ({
      ...prev,
      profession: profession || undefined
    }));
  };

  const handleCustomProfessionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomProfession(value);
    setFormData((prev: Partial<ClientData>) => ({
      ...prev,
      profession: value || undefined
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs du formulaire');
      return;
    }

    setLoading(true);

    try {
      // Traiter la civilité
      const civiliteValue = (formData.civilite === 'Mr' || formData.civilite === 'Mme') 
        ? formData.civilite 
        : undefined;

      // Nettoyer les données avant envoi
      const dataToSend = {
        client_type: 'societe' as const,
        email: formData.email || '',
        adresse: formData.adresse || '',
        telephone: formData.telephone?.replace(/\s/g, '') || '',
        civilite: civiliteValue,
        nom: formData.nom || undefined,
        prenom: formData.prenom || undefined,
        siren: formData.siren?.replace(/\s/g, '') || '',
        raison_sociale: formData.raison_sociale?.trim() || '',
        tva_intra: formData.tva_intra?.trim() || '',
        // code_postal: formData.code_postal || '',
        // ville: formData.ville || '',
        profession: formData.profession || undefined,
        show_in_directory: formData.show_in_directory || false,
        status: formData.status || 'active'
      };

      if (editingClient?.id) {
        const updatedClient = await clientService.updateClient(editingClient.id, dataToSend);
        toast.success('Société mise à jour avec succès');
        onClientAdded(updatedClient);
      } else {
        const newClient = await clientService.createClient(dataToSend);
        toast.success('Société créée avec succès');
        onClientAdded(newClient);
      }
      
      onClose();
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.join(', ');
        toast.error(errorMessages);
      } else {
        toast.error(error.message || 'Erreur lors de la sauvegarde');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" scrollable>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fs-5 fw-semibold">
          {editingClient ? 'Modifier la société' : 'Nouvelle société'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        <Form onSubmit={handleSubmit}>
          {/* Indicateur de type */}
          <div className="mb-4">
            <span className="badge bg-light text-dark px-3 py-2 rounded-pill">
              <i className="bi bi-building me-2"></i>
              Société
            </span>
            <small className="text-muted ms-2">* Champs obligatoires</small>
          </div>

          {/* Informations générales */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-3" style={{ fontSize: '0.9rem', letterSpacing: '0.3px', color: '#4a5568' }}>
              INFORMATIONS GÉNÉRALES
            </h6>
            
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary">
                    SIREN <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      name="siren"
                      value={formData.siren || ''}
                      onChange={handleSirenChange}
                      isInvalid={!!errors.siren}
                      placeholder="9 chiffres"
                      maxLength={9}
                      size="sm"
                    />
                    {loadingSiren && (
                      <Spinner 
                        size="sm" 
                        animation="border" 
                        variant="primary"
                        style={{ position: 'absolute', right: '10px', top: '8px' }}
                      />
                    )}
                    <Form.Control.Feedback type="invalid" className="small">
                      {errors.siren}
                    </Form.Control.Feedback>
                  </div>
                  <Form.Text className="text-muted">
                    Saisissez le SIREN pour auto-compléter les informations
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary">
                    Raison sociale <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="raison_sociale"
                    value={formData.raison_sociale || ''}
                    onChange={handleInputChange}
                    isInvalid={!!errors.raison_sociale}
                    placeholder="ex: SARL Dupont et Associés"
                    size="sm"
                  />
                  <Form.Control.Feedback type="invalid" className="small">
                    {errors.raison_sociale}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-1">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary">
                    TVA intra. <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="tva_intra"
                    value={formData.tva_intra || ''}
                    onChange={handleInputChange}
                    isInvalid={!!errors.tva_intra}
                    placeholder="FRXX999999999"
                    size="sm"
                  />
                  <Form.Control.Feedback type="invalid" className="small">
                    {errors.tva_intra}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary">Catégorie professionnelle</Form.Label>
                  <Form.Select
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    size="sm"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {Object.keys(PROFESSION_OPTIONS).map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Profession spécifique */}
            {selectedCategory && !showCustomProfession && (
              <Form.Group className="mt-3">
                <Form.Label className="small fw-medium text-secondary">Profession</Form.Label>
                <Form.Select
                  value={formData.profession || ''}
                  onChange={handleProfessionChange}
                  size="sm"
                >
                  <option value="">Sélectionner une profession</option>
                  {selectedCategory && PROFESSION_OPTIONS[selectedCategory]?.map(prof => (
                    <option key={prof} value={prof}>{prof}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {showCustomProfession && (
              <Form.Group className="mt-3">
                <Form.Label className="small fw-medium text-secondary">Précisez la profession</Form.Label>
                <Form.Control
                  type="text"
                  value={customProfession}
                  onChange={handleCustomProfessionChange}
                  placeholder="Intitulé de la profession"
                  size="sm"
                />
              </Form.Group>
            )}
          </div>

          {/* Coordonnées */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-3" style={{ fontSize: '0.9rem', letterSpacing: '0.3px', color: '#4a5568' }}>
              COORDONNÉES
            </h6>
            
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary">
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleInputChange}
                    isInvalid={!!errors.email}
                    placeholder="contact@entreprise.fr"
                    size="sm"
                  />
                  <Form.Control.Feedback type="invalid" className="small">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary">
                    Téléphone <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="telephone"
                    value={formData.telephone || ''}
                    onChange={handleInputChange}
                    isInvalid={!!errors.telephone}
                    placeholder="01 23 45 67 89"
                    size="sm"
                  />
                  <Form.Control.Feedback type="invalid" className="small">
                    {errors.telephone}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mt-3">
              <Form.Label className="small fw-medium text-secondary">
                Adresse <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="adresse"
                value={formData.adresse || ''}
                onChange={handleInputChange}
                isInvalid={!!errors.adresse}
                placeholder="Adresse complète"
                size="sm"
              />
              <Form.Control.Feedback type="invalid" className="small">
                {errors.adresse}
              </Form.Control.Feedback>
            </Form.Group>

            {/* <Row className="g-3 mt-1">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary">
                    Code postal <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="code_postal"
                    value={formData.code_postal || ''}
                    onChange={handleInputChange}
                    isInvalid={!!errors.code_postal}
                    placeholder="5 chiffres"
                    maxLength={5}
                    size="sm"
                  />
                  <Form.Control.Feedback type="invalid" className="small">
                    {errors.code_postal}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary">
                    Ville <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="ville"
                    value={formData.ville || ''}
                    onChange={handleInputChange}
                    isInvalid={!!errors.ville}
                    disabled={cities.length === 0}
                    size="sm"
                  >
                    <option value="">
                      {cities.length === 0 ? 'Saisissez un code postal' : 'Sélectionnez une ville'}
                    </option>
                    {cities.map((city, index) => (
                      <option key={index} value={city}>
                        {city}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid" className="small">
                    {errors.ville}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row> */}
          </div>

          {/* Représentant - Optionnel */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-3" style={{ fontSize: '0.9rem', letterSpacing: '0.3px', color: '#4a5568' }}>
              REPRÉSENTANT <span className="fw-normal text-muted">(optionnel)</span>
            </h6>
            
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary">Civilité</Form.Label>
                  <Form.Select
                    name="civilite"
                    value={formData.civilite || ''}
                    onChange={handleInputChange}
                    size="sm"
                  >
                    <option value="">-</option>
                    <option value="Mr">Mr</option>
                    <option value="Mme">Mme</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary">Nom</Form.Label>
                  <Form.Control
                    type="text"
                    name="nom"
                    value={formData.nom || ''}
                    onChange={handleInputChange}
                    placeholder="Nom"
                    size="sm"
                  />
                </Form.Group>
              </Col>
              
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary">Prénom</Form.Label>
                  <Form.Control
                    type="text"
                    name="prenom"
                    value={formData.prenom || ''}
                    onChange={handleInputChange}
                    placeholder="Prénom"
                    size="sm"
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Configuration */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-3" style={{ fontSize: '0.9rem', letterSpacing: '0.3px', color: '#4a5568' }}>
              CONFIGURATION
            </h6>
            
            <Row className="g-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary d-block">Statut</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="status-active"
                      name="status"
                      value="active"
                      checked={formData.status === 'active'}
                      onChange={handleInputChange}
                      label={<span className="small">Actif</span>}
                    />
                    <Form.Check
                      type="radio"
                      id="status-inactive"
                      name="status"
                      value="inactive"
                      checked={formData.status === 'inactive'}
                      onChange={handleInputChange}
                      label={<span className="small">Inactif</span>}
                    />
                  </div>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-medium text-secondary d-block">Annuaire public</Form.Label>
                  <Form.Check
                    type="switch"
                    id="show-in-directory"
                    name="show_in_directory"
                    checked={formData.show_in_directory || false}
                    onChange={handleInputChange}
                    label={<span className="small">Afficher dans l'annuaire</span>}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Actions */}
          <div className="d-flex gap-2 justify-content-end pt-3 border-top">
            <Button 
              variant="light" 
              onClick={onClose} 
              disabled={loading}
              size="sm"
              className="px-3"
            >
              Annuler
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading}
              size="sm"
              className="px-4"
              style={{ backgroundColor: '#4bb0c0', borderColor: '#4bb0c0' }}
            >
              {loading ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Chargement...
                </>
              ) : (
                editingClient ? 'Mettre à jour' : 'Créer'
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddClientModal;